import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Grid,
  Paper,
  Slider,
  Switch,
  TextField,
  Button,
  Tooltip,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  FormControlLabel,
  Chip,
} from '@mui/material';
import axios from 'axios';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import Header from "../../components/Header";

const getApiBase = () => {
  const configured = process.env.REACT_APP_API_BASE;
  if (configured === "" || configured === undefined || configured === null) return "";
  return configured.replace(/\/v1\/api\/?$/, "").replace(/\/$/, "");
};

const VisualAIConfig = () => {
  const theme = useTheme();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [selectedDetector, setSelectedDetector] = useState('shoplifting');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      const baseUrl = getApiBase();
      const res = await axios.get(`${baseUrl}/api/config/ai`, axiosConfig);
      setConfig(res.data);
    } catch (err) {
      console.error("Error fetching AI config:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      const baseUrl = getApiBase();
      await axios.post(`${baseUrl}/api/config/ai`, config, axiosConfig);
      setSnack({ open: true, message: 'Configuración aplicada con éxito', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: 'Error al guardar la configuración', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    const newConfig = { ...config };
    let current = newConfig;
    const parts = path.split('.');
    for (let i = 0; i < parts.length - 1; i++) {
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
    setConfig(newConfig);
  };

  if (loading) return <Box display="flex" justifyContent="center" mt="50px"><CircularProgress color="secondary" /></Box>;

  return (
    <Box m="20px">
      <Box className="scene-toolbar" display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Header title="CONFIGURACIÓN AI VISUAL" subtitle="Optimiza el rendimiento y precisión de tus algoritmos" />
        <Box display="flex" gap={2}>
          <Button startIcon={<RestartAltIcon />} onClick={fetchConfig} sx={{ color: "text.secondary" }}>Resetear</Button>
          <Button variant="contained" color="secondary" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Aplicar Cambios'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} mt={1}>
        {/* Left Panel: Settings */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ background: theme.custom.surface.base, borderRadius: "8px", p: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
              <Tab label="Detectores" />
              <Tab label="Performance" />
              <Tab label="Notificaciones" />
            </Tabs>

            {tab === 0 && (
              <Box>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: "text.primary" }}>Seleccionar Algoritmo</InputLabel>
                  <Select
                    value={selectedDetector}
                    onChange={(e) => setSelectedDetector(e.target.value)}
                    sx={{ color: "text.primary", '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' } }}
                  >
                    {config && config.detections && Object.keys(config.detections).map(det => (
                      <MenuItem key={det} value={det}>{det.toUpperCase()}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 600 }}>Umbral de Confianza (Threshold)</Typography>
                      <Tooltip title="Nivel de certeza mínimo para disparar una alerta">
                        <HelpOutlineIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                      </Tooltip>
                    </Box>
                    <Slider
                      value={(config?.detections?.[selectedDetector]?.confidence_threshold || config?.detections?.[selectedDetector]?.yowo_conf || 0.5)}
                      min={0.1} max={0.99} step={0.01}
                      valueLabelDisplay="auto"
                      onChange={(_, v) => updateField(`detections.${selectedDetector}.${config?.detections?.[selectedDetector]?.confidence_threshold ? 'confidence_threshold' : 'yowo_conf'}`, v)}
                      sx={{ color: "primary.main" }}
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 600 }}>Procesamiento en Tiempo Real</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>Desactivar para procesar en segundo plano y ahorrar CPU</Typography>
                    </Box>
                    <Switch checked={true} color="secondary" />
                  </Box>

                  <Box>
                    <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 600, mb: 1 }}>FPS de Procesamiento</Typography>
                    <Slider
                      value={config?.detections?.[selectedDetector]?.fps || 30}
                      min={1} max={60} step={1}
                      valueLabelDisplay="auto"
                      onChange={(_, v) => updateField(`detections.${selectedDetector}.fps`, v)}
                      sx={{ color: "primary.main" }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h6" color="#fff">Global Engine Settings</Typography>
                <TextField label="Max GPU Memory (%)" type="number" defaultValue={80} fullWidth sx={{ input: { color: "text.primary" }, label: { color: "text.secondary" } }} />
                <FormControlLabel control={<Switch defaultChecked />} label={<Typography color="#fff">Aceleración por Hardware (TensorRT)</Typography>} />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Panel: Preview */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ background: (theme) => theme.custom.surface.base, borderRadius: "8px", p: 0, overflow: 'hidden', height: '100%', border: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ p: 2, background: (theme) => theme.custom.surface.base, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" color="#fff">Vista Previa de Detección</Typography>
              <Chip label="SIMULACIÓN ACTIVA" size="small" color="secondary" variant="outlined" />
            </Box>
            <Box sx={{ position: 'relative', pt: '75%', background: '#000' }}>
              <img
                src="https://images.unsplash.com/photo-1557597774-9d2739f85a94?q=80&w=600&auto=format&fit=crop"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }}
                alt="preview"
              />
              <Box sx={{ position: 'absolute', top: '20%', left: '30%', width: '100px', height: '150px', border: '2px solid #6FFFE9', borderRadius: '4px' }}>
                <Typography variant="caption" sx={{ position: 'absolute', top: -20, left: 0, background: '#6FFFE9', color: '#000', px: 0.5, fontWeight: 700 }}>
                  PERSON {Math.round((config?.detections?.[selectedDetector]?.confidence_threshold || 0.5) * 100)}%
                </Typography>
              </Box>
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PlayCircleOutlineIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.2)' }} />
              </Box>
            </Box>
            <Box p={3}>
              <Typography variant="caption" color="rgba(255,255,255,0.4)">
                * Esta es una previsualización simulada basada en los umbrales actuales. El rendimiento real puede variar según la GPU y la calidad del stream.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default VisualAIConfig;
